import { type AssetAction, assetActionsFor } from "@edm/domain";
import { ASSET_STATUS_LABELS, hasPermission } from "@edm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Eye, Plus, Printer, RefreshCw, Search, Wrench } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AssetImportDialog } from "@/components/asset-import-dialog";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { SignaturePad } from "@/components/signature-pad";
import { StatCard } from "@/components/stat-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError, apiFetch } from "@/lib/api";
import { useMe } from "@/lib/auth";
import {
  type AssetDetail,
  type AssetRow,
  createResource,
  listResource,
  type MaterialRow,
  updateResource,
  useReferenceData,
} from "@/lib/master-data";
import { cn, formatDate } from "@/lib/utils";

const ACTION_LABELS: Record<AssetAction, string> = {
  accept: "验收",
  assign: "领用",
  borrow: "借用",
  return: "申请归还",
  complete_return: "完成归还",
  start_repair: "送修",
  finish_repair: "完成维修",
  retire: "退役",
  dispose: "处置",
  report_lost: "报失",
  recover_lost: "找回入库",
};

const STATUS_VARIANT: Record<string, "default" | "success" | "warning" | "danger" | "secondary"> = {
  pending_acceptance: "warning",
  in_stock: "success",
  assigned: "default",
  borrowed: "default",
  return_pending: "warning",
  repairing: "warning",
  pending_disposal: "danger",
  disposed: "secondary",
  lost: "danger",
};

interface InboundForm {
  materialId: string;
  quantity: string;
  price: string;
  inboundDate: string;
  acceptanceRequired: boolean;
  condition: "new" | "used";
}

interface InstanceDetail {
  key: string;
  serialNumber: string;
  financialCardNo: string;
}

function emptyInstance(): InstanceDetail {
  return { key: crypto.randomUUID(), serialNumber: "", financialCardNo: "" };
}

export function AssetsPage() {
  const { data: me } = useMe();
  const reference = useReferenceData();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [importOpen, setImportOpen] = useState(false);
  const PAGE_SIZE = 20;
  const [status, setStatus] = useState("");
  const [inboundOpen, setInboundOpen] = useState(false);
  const [transitionOpen, setTransitionOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [returnSignature, setReturnSignature] = useState<string | null>(null);
  const [customValues, setCustomValues] = useState<Record<string, string | boolean>>({});
  const [editForm, setEditForm] = useState({
    serialNumber: "",
    financialCardNo: "",
    warrantyExpiry: "",
    purchaseOrderNo: "",
    contractNo: "",
    modelSpec: "",
    location: "",
    assetManagerId: "",
    costCenterId: "",
  });
  const [selectedAsset, setSelectedAsset] = useState<AssetRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [inbound, setInbound] = useState<InboundForm>({
    materialId: "",
    quantity: "1",
    price: "",
    inboundDate: new Date().toISOString().slice(0, 10),
    acceptanceRequired: true,
    condition: "new",
  });
  const [instances, setInstances] = useState<InstanceDetail[]>([emptyInstance()]);
  const [transition, setTransition] = useState({
    action: "assign" as AssetAction,
    holderId: "",
    hostname: "",
    disposalMethod: "recycled",
    disposalNote: "",
    dataCleared: false,
  });

  const canManage = Boolean(me && hasPermission(me.permissions, "asset:manage"));
  const canLabel = Boolean(me && hasPermission(me.permissions, "label:manage"));
  const detailQuery = useQuery({
    queryKey: ["asset-detail", detailId],
    queryFn: () => apiFetch<AssetDetail>(`/api/v1/assets/${detailId}`),
    enabled: Boolean(detailId),
  });
  const customFieldsQuery = useQuery({
    queryKey: ["asset-custom-fields", detailId],
    queryFn: () =>
      apiFetch<{
        definitions: {
          id: string;
          code: string;
          name: string;
          dataType: string;
          required: boolean;
          options: string[];
        }[];
        values: { definitionId: string; value: unknown }[];
      }>(`/api/v1/assets/${detailId}/custom-fields`),
    enabled: Boolean(detailId),
  });
  useEffect(() => {
    if (!customFieldsQuery.data) return;
    const next: Record<string, string | boolean> = {};
    for (const definition of customFieldsQuery.data.definitions) {
      const found = customFieldsQuery.data.values.find((row) => row.definitionId === definition.id);
      next[definition.id] =
        definition.dataType === "boolean" ? Boolean(found?.value) : String(found?.value ?? "");
    }
    setCustomValues(next);
  }, [customFieldsQuery.data]);
  useEffect(() => {
    const asset = detailQuery.data?.asset;
    if (!asset) return;
    setEditForm({
      serialNumber: asset.serialNumber ?? "",
      financialCardNo: asset.financialCardNo ?? "",
      warrantyExpiry: asset.warrantyExpiry ?? "",
      purchaseOrderNo: asset.purchaseOrderNo ?? "",
      contractNo: asset.contractNo ?? "",
      modelSpec: asset.modelSpec ?? "",
      location: asset.locationText ?? "",
      assetManagerId: asset.assetManagerId ?? "",
      costCenterId: asset.costCenterId ?? "",
    });
  }, [detailQuery.data]);
  const assetsQuery = useQuery({
    queryKey: ["assets", status, query, page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      if (status) params.set("status", status);
      if (query.trim()) params.set("q", query.trim());
      return apiFetch<{ items: AssetRow[]; total: number }>(`/api/v1/assets?${params.toString()}`);
    },
  });
  const materialsQuery = useQuery({
    queryKey: ["materials"],
    queryFn: () => listResource<MaterialRow>("/api/v1/materials"),
  });

  const assets = assetsQuery.data?.items ?? [];
  const totalAssets = assetsQuery.data?.total ?? 0;
  const materials = materialsQuery.data ?? [];
  const assetMaterials = materials.filter(
    (item) => item.category === "asset" && item.status === "active",
  );
  const materialById = useMemo(
    () => new Map(materials.map((item) => [item.id, item])),
    [materials],
  );
  const users = reference.data?.users ?? [];
  const departments = reference.data?.departments ?? [];

  const totalPages = Math.max(1, Math.ceil(totalAssets / PAGE_SIZE));
  const pageRows = assets;

  const stats = useMemo(() => {
    const count = (value: string) => assets.filter((row) => row.status === value).length;
    return {
      total: assets.length,
      inStock: count("in_stock"),
      assigned: count("assigned") + count("borrowed"),
      repair: count("repairing"),
      disposed: count("disposed"),
    };
  }, [assets]);

  function setQuantity(value: string) {
    const quantity = Math.max(1, Math.min(20, Number(value) || 1));
    setInbound({ ...inbound, quantity: String(quantity) });
    setInstances((current) => {
      const next = [...current];
      while (next.length < quantity) next.push(emptyInstance());
      return next.slice(0, quantity);
    });
  }

  function setInstance(index: number, key: keyof InstanceDetail, value: string) {
    setInstances((current) =>
      current.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)),
    );
  }

  async function submitInbound() {
    if (!inbound.materialId) return toast.error("请选择资产物料");
    if (!inbound.price.trim()) return toast.error("请填写本次采购单价");
    setSaving(true);
    try {
      await createResource(`/api/v1/materials/${inbound.materialId}/inbound`, {
        quantity: Number(inbound.quantity),
        purchaseUnitPriceYuan: Number(inbound.price),
        inboundDate: inbound.inboundDate,
        acceptanceRequired: inbound.acceptanceRequired,
        condition: inbound.condition,
        instanceDetails: instances.map((item) => ({
          serialNumber: item.serialNumber.trim() || null,
          financialCardNo: item.financialCardNo.trim() || null,
          minorIssues: [],
        })),
      });
      toast.success("资产已入库");
      setInboundOpen(false);
      setInstances([emptyInstance()]);
      setInbound({ ...inbound, quantity: "1", price: "" });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["assets"] }),
        queryClient.invalidateQueries({ queryKey: ["reference-data"] }),
      ]);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "入库失败，请检查填写内容");
    } finally {
      setSaving(false);
    }
  }

  function openTransition(asset: AssetRow) {
    const actions = assetActionsFor(asset.status as never);
    setSelectedAsset(asset);
    setTransition({
      action: actions[0] ?? "assign",
      holderId: asset.holderId ?? "",
      hostname: asset.hostname ?? "",
      disposalMethod: "recycled",
      disposalNote: "",
      dataCleared: false,
    });
    setTransitionOpen(true);
  }

  async function submitTransition() {
    if (!selectedAsset) return;
    setSaving(true);
    try {
      await createResource(`/api/v1/assets/${selectedAsset.id}/transitions`, {
        action: transition.action,
        holderId: transition.holderId || null,
        hostname: transition.hostname || null,
        disposalMethod: transition.action === "dispose" ? transition.disposalMethod : undefined,
        disposalNote: transition.action === "dispose" ? transition.disposalNote || null : undefined,
        dataCleared: transition.action === "dispose" ? transition.dataCleared : undefined,
        confirmationText: `${ACTION_LABELS[transition.action]}确认`,
      });
      toast.success(`${ACTION_LABELS[transition.action]}已完成`);
      setTransitionOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["assets"] });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "办理失败，请刷新后重试");
    } finally {
      setSaving(false);
    }
  }

  async function saveAssetDetail() {
    if (!detailId) return;
    setDetailBusy(true);
    try {
      await updateResource(`/api/v1/assets/${detailId}`, {
        serialNumber: editForm.serialNumber || null,
        financialCardNo: editForm.financialCardNo || null,
        warrantyExpiry: editForm.warrantyExpiry || null,
        purchaseOrderNo: editForm.purchaseOrderNo || null,
        contractNo: editForm.contractNo || null,
        modelSpec: editForm.modelSpec || null,
        location: editForm.location || null,
        assetManagerId: editForm.assetManagerId || null,
        costCenterId: editForm.costCenterId || null,
      });
      toast.success("资产信息已更新");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["assets"] }),
        queryClient.invalidateQueries({ queryKey: ["asset-detail", detailId] }),
      ]);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "保存失败");
    } finally {
      setDetailBusy(false);
    }
  }

  async function decideReturn(decision: "confirm" | "reject") {
    const confirmationId = detailQuery.data?.returnConfirmation?.id;
    if (!confirmationId) return;
    setDetailBusy(true);
    try {
      await createResource(`/api/v1/return-confirmations/${confirmationId}/decide`, {
        decision,
        reason: decision === "reject" ? returnReason : null,
        signatureData: decision === "confirm" ? returnSignature : null,
      });
      toast.success(decision === "confirm" ? "已确认归还，资产回到在库" : "已拒绝归还");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["assets"] }),
        queryClient.invalidateQueries({ queryKey: ["asset-detail", detailId] }),
        queryClient.invalidateQueries({ queryKey: ["reminders"] }),
      ]);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "归还确认失败");
    } finally {
      setDetailBusy(false);
    }
  }

  async function createLabelTask(asset: AssetRow) {
    try {
      await createResource("/api/v1/label-print-tasks", {
        targetType: "asset",
        targetId: asset.id,
        quantity: 1,
        businessSource: "asset_detail",
      });
      toast.success("标签任务已创建");
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "标签任务创建失败");
    }
  }

  const allowedActions = selectedAsset ? assetActionsFor(selectedAsset.status as never) : [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="资产台账"
        description="资产按实例逐条管理；入库后可按状态机办理验收、领用、借用、归还、维修、退役与处置。"
        actions={
          <>
            <Button
              variant="outline"
              onClick={async () => {
                const response = await fetch("/api/v1/assets/export");
                if (!response.ok) return;
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `assets-${new Date().toISOString().slice(0, 10)}.csv`;
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              <Download className="h-4 w-4" />
              导出 CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => assetsQuery.refetch()}
              disabled={assetsQuery.isFetching}
            >
              <RefreshCw className={cn("h-4 w-4", assetsQuery.isFetching && "animate-spin")} />
              刷新
            </Button>
            {canManage ? (
              <>
                <Button variant="outline" onClick={() => setImportOpen(true)}>
                  <Download className="h-4 w-4" />
                  批量导入
                </Button>
                <Button onClick={() => setInboundOpen(true)}>
                  <Plus className="h-4 w-4" />
                  登记入库
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="资产总量" value={stats.total} icon={Wrench} />
        <StatCard label="在库可用" value={stats.inStock} icon={Wrench} accent="success" />
        <StatCard label="使用中 / 借用" value={stats.assigned} icon={Wrench} accent="warning" />
        <StatCard label="已处置" value={stats.disposed} icon={Wrench} accent="danger" />
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>资产列表</CardTitle>
            <CardDescription>搜索资产编号、序列号或主机名；处置后不可重新领用。</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPage(1);
                }}
                placeholder="搜索资产编号 / 序列号"
                className="pl-9"
              />
            </div>
            <select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
              className="h-10 rounded-md border border-input bg-card px-3 text-sm"
            >
              <option value="">全部状态</option>
              {Object.entries(ASSET_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {assetsQuery.isLoading ? (
            <div className="space-y-2">
              {["a1", "a2", "a3", "a4"].map((key) => (
                <Skeleton key={key} className="h-11 w-full" />
              ))}
            </div>
          ) : totalAssets === 0 ? (
            <EmptyState title="暂无资产" description="登记入库后，资产会出现在这里。" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>资产编号</TableHead>
                  <TableHead>物料</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>使用人</TableHead>
                  <TableHead>设备管理人员</TableHead>
                  <TableHead>序列号</TableHead>
                  <TableHead>借用到期</TableHead>
                  <TableHead>入库日期</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((asset) => (
                  <TableRow key={asset.id}>
                    <TableCell className="font-mono text-xs">{asset.assetCode}</TableCell>
                    <TableCell className="font-medium">
                      {materialById.get(asset.materialId)?.name ?? "—"}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {materialById.get(asset.materialId)?.code ?? ""}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[asset.status] ?? "secondary"}>
                        {ASSET_STATUS_LABELS[asset.status as keyof typeof ASSET_STATUS_LABELS] ??
                          asset.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {users.find((user) => user.id === asset.holderId)?.displayName ?? "—"}
                    </TableCell>
                    <TableCell>
                      {users.find((user) => user.id === asset.assetManagerId)?.displayName ?? "—"}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{asset.serialNumber ?? "—"}</TableCell>
                    <TableCell>{asset.borrowDueAt ? formatDate(asset.borrowDueAt) : "—"}</TableCell>
                    <TableCell>{formatDate(asset.inboundDate)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setDetailId(asset.id)}>
                          <Eye className="h-3.5 w-3.5" />
                          详情
                        </Button>
                        {canLabel ? (
                          <Button variant="ghost" size="sm" onClick={() => createLabelTask(asset)}>
                            <Printer className="h-3.5 w-3.5" />
                            标签
                          </Button>
                        ) : null}
                        {canManage ? (
                          <Button variant="ghost" size="sm" onClick={() => openTransition(asset)}>
                            办理
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!assetsQuery.isLoading && totalAssets > 0 ? (
            <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
              <span>
                共 {totalAssets} 条 · 第 {page} / {totalPages} 页
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((value) => value - 1)}
                >
                  上一页
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((value) => value + 1)}
                >
                  下一页
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <AssetImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => {
          queryClient.invalidateQueries({ queryKey: ["assets"] });
          queryClient.invalidateQueries({ queryKey: ["reference-data"] });
        }}
      />

      <Dialog open={inboundOpen} onOpenChange={setInboundOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>登记资产入库</DialogTitle>
            <DialogDescription>
              公共信息填写一次，逐台明细单独登记；序列号/财务卡片号重复会在提交时拦截。
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label>资产物料</Label>
              <select
                value={inbound.materialId}
                onChange={(event) => setInbound({ ...inbound, materialId: event.target.value })}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">请选择资产物料</option>
                {assetMaterials.map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.code} · {material.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>数量</Label>
              <Input
                type="number"
                min="1"
                max="20"
                value={inbound.quantity}
                onChange={(event) => setQuantity(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>本次采购单价（元）</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={inbound.price}
                onChange={(event) => setInbound({ ...inbound, price: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>入库日期</Label>
              <Input
                type="date"
                value={inbound.inboundDate}
                onChange={(event) => setInbound({ ...inbound, inboundDate: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>是否全新</Label>
              <select
                value={inbound.condition}
                onChange={(event) =>
                  setInbound({ ...inbound, condition: event.target.value as "new" | "used" })
                }
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="new">全新</option>
                <option value="used">非全新</option>
              </select>
            </div>
            <label className="flex items-center gap-2 pt-7 text-sm">
              <input
                type="checkbox"
                checked={inbound.acceptanceRequired}
                onChange={(event) =>
                  setInbound({ ...inbound, acceptanceRequired: event.target.checked })
                }
                className="h-4 w-4"
              />
              需要验收入库
            </label>
          </div>
          <div className="space-y-2">
            <Label>逐台明细（序列号 / 财务卡片编号）</Label>
            <div className="max-h-56 space-y-2 overflow-auto">
              {instances.map((instance, index) => (
                <div key={instance.key} className="grid gap-2 sm:grid-cols-[60px_1fr_1fr]">
                  <span className="pt-2 text-xs text-muted-foreground">#{index + 1}</span>
                  <Input
                    placeholder="序列号（选填）"
                    value={instance.serialNumber}
                    onChange={(event) => setInstance(index, "serialNumber", event.target.value)}
                  />
                  <Input
                    placeholder="财务卡片编号（选填）"
                    value={instance.financialCardNo}
                    onChange={(event) => setInstance(index, "financialCardNo", event.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInboundOpen(false)}>
              取消
            </Button>
            <Button onClick={submitInbound} disabled={saving}>
              {saving ? "入库中…" : "确认入库"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(detailId)} onOpenChange={(open) => !open && setDetailId(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>资产详情：{detailQuery.data?.asset.assetCode ?? ""}</DialogTitle>
            <DialogDescription>
              编辑资产属性；状态变化请走“办理”。生命周期流水只读。
            </DialogDescription>
          </DialogHeader>
          {detailQuery.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : detailQuery.data ? (
            <div className="max-h-[60vh] space-y-4 overflow-auto pr-1">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>序列号</Label>
                  <Input
                    value={editForm.serialNumber}
                    onChange={(event) =>
                      setEditForm({ ...editForm, serialNumber: event.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>财务卡片号</Label>
                  <Input
                    value={editForm.financialCardNo}
                    onChange={(event) =>
                      setEditForm({ ...editForm, financialCardNo: event.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>保修到期</Label>
                  <Input
                    type="date"
                    value={editForm.warrantyExpiry}
                    onChange={(event) =>
                      setEditForm({ ...editForm, warrantyExpiry: event.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>采购单号</Label>
                  <Input
                    value={editForm.purchaseOrderNo}
                    onChange={(event) =>
                      setEditForm({ ...editForm, purchaseOrderNo: event.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>合同号</Label>
                  <Input
                    value={editForm.contractNo}
                    onChange={(event) =>
                      setEditForm({ ...editForm, contractNo: event.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>型号规格</Label>
                  <Input
                    value={editForm.modelSpec}
                    onChange={(event) =>
                      setEditForm({ ...editForm, modelSpec: event.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>使用位置</Label>
                  <Input
                    value={editForm.location}
                    onChange={(event) => setEditForm({ ...editForm, location: event.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>设备管理员</Label>
                  <select
                    value={editForm.assetManagerId}
                    onChange={(event) =>
                      setEditForm({ ...editForm, assetManagerId: event.target.value })
                    }
                    className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                  >
                    <option value="">未指定</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.displayName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>成本中心</Label>
                  <select
                    value={editForm.costCenterId}
                    onChange={(event) =>
                      setEditForm({ ...editForm, costCenterId: event.target.value })
                    }
                    className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                  >
                    <option value="">未指定</option>
                    {departments.map((department) => (
                      <option key={department.id} value={department.id}>
                        {department.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>借用到期</Label>
                  <Input
                    readOnly
                    value={
                      detailQuery.data.asset.borrowDueAt
                        ? detailQuery.data.asset.borrowDueAt.slice(0, 10)
                        : "—"
                    }
                  />
                </div>
              </div>
              {detailQuery.data.returnConfirmation ? (
                <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-medium text-amber-900">归还待接收人确认</p>
                  <p className="text-xs text-amber-800">
                    接收人：
                    {users.find(
                      (user) => user.id === detailQuery.data?.returnConfirmation?.holderId,
                    )?.displayName ?? "—"}{" "}
                    · 截止 {formatDate(detailQuery.data.returnConfirmation.dueAt)}
                  </p>
                  {detailQuery.data.returnConfirmation.holderId === me?.user.id ? (
                    <>
                      <Input
                        value={returnReason}
                        onChange={(event) => setReturnReason(event.target.value)}
                        placeholder="拒绝时填写原因"
                      />
                      <SignaturePad onChange={setReturnSignature} />
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => decideReturn("reject")}
                          disabled={detailBusy}
                        >
                          拒绝归还
                        </Button>
                        <Button onClick={() => decideReturn("confirm")} disabled={detailBusy}>
                          确认归还
                        </Button>
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-amber-800">等待接收人确认；管理员可代为完成归还。</p>
                  )}
                </div>
              ) : null}

              {customFieldsQuery.data && customFieldsQuery.data.definitions.length > 0 ? (
                <div className="space-y-3 rounded-lg border border-border p-3">
                  <p className="text-sm font-medium">自定义字段</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {customFieldsQuery.data.definitions.map((definition) => (
                      <div key={definition.id} className="space-y-1">
                        <Label>
                          {definition.name}
                          {definition.required ? " *" : ""}
                        </Label>
                        {definition.dataType === "boolean" ? (
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              disabled={!canManage}
                              checked={Boolean(customValues[definition.id])}
                              onChange={(event) =>
                                setCustomValues({
                                  ...customValues,
                                  [definition.id]: event.target.checked,
                                })
                              }
                            />
                            是
                          </label>
                        ) : definition.dataType === "select" ? (
                          <select
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                            disabled={!canManage}
                            value={String(customValues[definition.id] ?? "")}
                            onChange={(event) =>
                              setCustomValues({
                                ...customValues,
                                [definition.id]: event.target.value,
                              })
                            }
                          >
                            <option value="">未选择</option>
                            {definition.options.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            type={
                              definition.dataType === "number"
                                ? "number"
                                : definition.dataType === "date"
                                  ? "date"
                                  : "text"
                            }
                            disabled={!canManage}
                            value={String(customValues[definition.id] ?? "")}
                            onChange={(event) =>
                              setCustomValues({
                                ...customValues,
                                [definition.id]: event.target.value,
                              })
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  {canManage ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          await apiFetch(`/api/v1/assets/${detailId}/custom-fields`, {
                            method: "PUT",
                            body: JSON.stringify({
                              values: customFieldsQuery.data.definitions.map((definition) => ({
                                definitionId: definition.id,
                                value:
                                  definition.dataType === "boolean"
                                    ? Boolean(customValues[definition.id])
                                    : (customValues[definition.id] ?? null),
                              })),
                            }),
                          });
                          await customFieldsQuery.refetch();
                          toast.success("自定义字段已保存");
                        } catch (error) {
                          toast.error(error instanceof Error ? error.message : "保存失败");
                        }
                      }}
                    >
                      保存自定义字段
                    </Button>
                  ) : null}
                </div>
              ) : null}

              <div>
                <p className="mb-2 text-sm font-medium">生命周期流水</p>
                <div className="space-y-1">
                  {detailQuery.data.transitions.length === 0 ? (
                    <p className="text-xs text-muted-foreground">暂无流水</p>
                  ) : null}
                  {detailQuery.data.transitions.map((transition) => (
                    <div
                      key={transition.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded border border-border px-3 py-2 text-xs"
                    >
                      <span className="font-mono">
                        {transition.fromStatus} → {transition.toStatus}
                      </span>
                      <span>{transition.action}</span>
                      <span className="text-muted-foreground">
                        {formatDate(transition.occurredAt)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailId(null)}>
              关闭
            </Button>
            {canManage ? (
              <Button onClick={saveAssetDetail} disabled={detailBusy}>
                保存资产信息
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={transitionOpen} onOpenChange={setTransitionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>办理资产：{selectedAsset?.assetCode}</DialogTitle>
            <DialogDescription>
              当前状态：
              {ASSET_STATUS_LABELS[selectedAsset?.status as keyof typeof ASSET_STATUS_LABELS] ??
                "—"}
              。状态变化会写入生命周期与审计。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>办理动作</Label>
              <select
                value={transition.action}
                onChange={(event) =>
                  setTransition({ ...transition, action: event.target.value as AssetAction })
                }
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                {allowedActions.map((action) => (
                  <option key={action} value={action}>
                    {ACTION_LABELS[action]}
                  </option>
                ))}
              </select>
            </div>
            {transition.action === "assign" || transition.action === "borrow" ? (
              <>
                <div className="space-y-2">
                  <Label>使用人员</Label>
                  <select
                    value={transition.holderId}
                    onChange={(event) =>
                      setTransition({ ...transition, holderId: event.target.value })
                    }
                    className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                  >
                    <option value="">请选择</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.displayName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>主机名（选填）</Label>
                  <Input
                    value={transition.hostname}
                    onChange={(event) =>
                      setTransition({ ...transition, hostname: event.target.value })
                    }
                  />
                </div>
              </>
            ) : null}
            {transition.action === "dispose" ? (
              <>
                <div className="space-y-2">
                  <Label>处置方式</Label>
                  <select
                    value={transition.disposalMethod}
                    onChange={(event) =>
                      setTransition({ ...transition, disposalMethod: event.target.value })
                    }
                    className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                  >
                    <option value="sold">出售</option>
                    <option value="recycled">回收</option>
                    <option value="scrapped">报废</option>
                    <option value="other">其他</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={transition.dataCleared}
                    onChange={(event) =>
                      setTransition({ ...transition, dataCleared: event.target.checked })
                    }
                    className="h-4 w-4"
                  />
                  已确认完成数据清除
                </label>
                <div className="space-y-2">
                  <Label>处置说明</Label>
                  <Input
                    value={transition.disposalNote}
                    onChange={(event) =>
                      setTransition({ ...transition, disposalNote: event.target.value })
                    }
                  />
                </div>
              </>
            ) : null}
            <div className="rounded-lg bg-muted/60 p-3 text-xs leading-5 text-muted-foreground">
              成本中心：
              {departments.find((item) => item.id === selectedAsset?.costCenterId)?.name ?? "—"} ·
              使用位置：{selectedAsset?.locationText ?? "—"}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransitionOpen(false)}>
              取消
            </Button>
            <Button onClick={submitTransition} disabled={saving}>
              {saving ? "办理中…" : `确认${ACTION_LABELS[transition.action]}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
